"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
/**
 * SnapPage - Cohort Apply page
 *
 * Three-section form matching Angular's SnapComponent:
 * Section 1: Cohort Information (Org, Academic Program, Program/Spec/Track)
 * Section 2: Plan (TermPlanningGrid)
 * Section 3: Students (UUID textarea, Export, Apply)
 */
const react_1 = require("react");
const react_router_dom_1 = require("react-router-dom");
const AuthProvider_1 = require("../../providers/AuthProvider");
const ToastProvider_1 = require("../../providers/ToastProvider");
const OrganizationSelect_1 = require("../../components/OrganizationSelect");
const ProgramSelect_1 = require("../../components/ProgramSelect");
const EverspringMappingSelect_1 = require("../../components/EverspringMappingSelect");
const StudentIdTextarea_1 = require("../../components/StudentIdTextarea");
const TermPlanningGrid_1 = require("../../components/TermPlanningGrid");
const ValidationErrors_1 = require("../../components/ValidationErrors");
const Button_1 = require("../../components/Button");
const Modal_1 = require("../../components/Modal");
const LoadingSpinner_1 = require("../../components/LoadingSpinner");
const termApi_1 = require("../../helpers/termApi");
const courseApi_1 = require("../../helpers/courseApi");
const course_1 = require("../../models/course");
const degreePlanApi_1 = require("../../helpers/degreePlanApi");
const apiReverseMappers_1 = require("../../helpers/apiReverseMappers");
const degreePlanPrint_1 = require("../../helpers/degreePlanPrint");
const ValidationExceptionsModal_1 = require("../../components/ValidationExceptionsModal");
require("./SnapPage.scss");
const UUID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const SnapPage = () => {
    const navigate = (0, react_router_dom_1.useNavigate)();
    const { user } = (0, AuthProvider_1.useAuth)();
    const { success, error: showError } = (0, ToastProvider_1.useToast)();
    // Section 1: Cohort Information
    const [selectedOrganization, setSelectedOrganization] = (0, react_1.useState)(null);
    const [selectedProgram, setSelectedProgram] = (0, react_1.useState)(null);
    const [everspringProgram, setEverspringProgram] = (0, react_1.useState)('');
    const [everspringSpecialization, setEverspringSpecialization] = (0, react_1.useState)('');
    const [everspringTrack, setEverspringTrack] = (0, react_1.useState)('');
    // Section 2: Plan
    const [termPlans, setTermPlans] = (0, react_1.useState)([]);
    // Section 3: Students
    const [studentIdsText, setStudentIdsText] = (0, react_1.useState)('');
    // Data state
    const [availableTerms, setAvailableTerms] = (0, react_1.useState)([]);
    const [availableCourses, setAvailableCourses] = (0, react_1.useState)([]);
    const [loadingTerms, setLoadingTerms] = (0, react_1.useState)(false);
    const [loadingCourses, setLoadingCourses] = (0, react_1.useState)(false);
    // Validation and submission
    const [validationErrors, setValidationErrors] = (0, react_1.useState)([]);
    const [applyError, setApplyError] = (0, react_1.useState)(null);
    const [isSubmitting, setIsSubmitting] = (0, react_1.useState)(false);
    const [isExporting, setIsExporting] = (0, react_1.useState)(false);
    const [showConfirmModal, setShowConfirmModal] = (0, react_1.useState)(false);
    const [showCohortOnlyModal, setShowCohortOnlyModal] = (0, react_1.useState)(false);
    const [showErrorModal, setShowErrorModal] = (0, react_1.useState)(false);
    const [serverErrors, setServerErrors] = (0, react_1.useState)(null);
    const [validatedStudentIds, setValidatedStudentIds] = (0, react_1.useState)([]);
    // Load terms and courses when organization changes
    (0, react_1.useEffect)(() => {
        if (!selectedOrganization) {
            setAvailableTerms([]);
            setAvailableCourses([]);
            return;
        }
        let cancelled = false;
        const loadData = () => __awaiter(void 0, void 0, void 0, function* () {
            setLoadingTerms(true);
            setLoadingCourses(true);
            try {
                const [terms, courses] = yield Promise.all([
                    termApi_1.termApi.getTermsByOrganization(selectedOrganization.id),
                    courseApi_1.courseApi.getCoursesByOrganization(selectedOrganization.id),
                ]);
                if (!cancelled) {
                    setAvailableTerms(terms);
                    setAvailableCourses(courses.filter(c => !course_1.CourseHelpers.isLOA(c)));
                }
            }
            catch (err) {
                if (!cancelled) {
                    showError(err instanceof Error ? err.message : 'Failed to load data');
                }
            }
            finally {
                if (!cancelled) {
                    setLoadingTerms(false);
                    setLoadingCourses(false);
                }
            }
        });
        loadData();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOrganization]);
    const handleOrganizationChange = (0, react_1.useCallback)((orgId, org) => {
        setSelectedOrganization(org);
        setSelectedProgram(null);
        setEverspringProgram('');
        setEverspringSpecialization('');
        setEverspringTrack('');
        setTermPlans([]);
        setStudentIdsText('');
        setApplyError(null);
    }, []);
    const handleProgramChange = (0, react_1.useCallback)((programId, program) => {
        setSelectedProgram(program);
        setEverspringProgram('');
        setEverspringSpecialization('');
        setEverspringTrack('');
    }, []);
    const parseStudentIds = (0, react_1.useCallback)(() => {
        return studentIdsText
            .split('\n')
            .map((line) => line.trim())
            .filter((line) => line.length > 0);
    }, [studentIdsText]);
    const validate = (0, react_1.useCallback)(() => {
        const errors = [];
        if (!selectedOrganization) {
            errors.push({ id: 'org', message: 'Please select an organization' });
        }
        if (!selectedProgram) {
            errors.push({ id: 'program', message: 'Please select an academic program' });
        }
        if (!everspringProgram) {
            errors.push({ id: 'everspring-program', message: 'Please select a program' });
        }
        if (termPlans.length === 0) {
            errors.push({ id: 'terms', message: 'Please add at least one term to the plan' });
        }
        // Check terms have courses
        const termsWithoutCourses = termPlans.filter((tp) => tp.courses.length === 0);
        if (termsWithoutCourses.length > 0) {
            errors.push({
                id: 'term-courses',
                message: `${termsWithoutCourses.length} term(s) have no courses assigned`,
            });
        }
        // Check for duplicate terms
        const termIds = termPlans.map((tp) => tp.term.id);
        const uniqueTermIds = new Set(termIds);
        if (termIds.length !== uniqueTermIds.size) {
            errors.push({ id: 'duplicate-terms', message: 'Duplicate terms found in plan' });
        }
        // Validate student IDs if provided
        const studentIds = parseStudentIds();
        if (studentIds.length > 0) {
            const invalidIds = studentIds.filter((id) => !UUID_PATTERN.test(id));
            if (invalidIds.length > 0) {
                errors.push({
                    id: 'invalid-ids',
                    message: `${invalidIds.length} student ID(s) have invalid UUID format`,
                });
            }
            // Check for duplicates
            const seen = new Set();
            const dupes = [];
            studentIds.forEach((id) => {
                if (seen.has(id.toLowerCase()))
                    dupes.push(id);
                seen.add(id.toLowerCase());
            });
            if (dupes.length > 0) {
                errors.push({ id: 'duplicate-ids', message: `${dupes.length} duplicate student ID(s) found` });
            }
        }
        setValidationErrors(errors);
        return errors.length === 0;
    }, [selectedOrganization, selectedProgram, everspringProgram, termPlans, parseStudentIds]);
    const handleApply = (0, react_1.useCallback)(() => {
        setApplyError(null);
        if (!validate())
            return;
        const studentIds = parseStudentIds();
        setValidatedStudentIds(studentIds);
        if (studentIds.length === 0) {
            setShowCohortOnlyModal(true);
        }
        else {
            setShowConfirmModal(true);
        }
    }, [validate, parseStudentIds]);
    /**
     * Build the snap payload from current form state.
     * Falls back to selectedOrganization.tenantId when user.tenantId is absent
     * (matches Angular: tenantId = degreePlan.tenantId || organization.tenantId).
     */
    const buildCurrentSnapPayload = (0, react_1.useCallback)((studentIds) => {
        const tenantId = (user === null || user === void 0 ? void 0 : user.tenantId) || selectedOrganization.tenantId;
        // Calculate graduation date: last term's end date + 1 day (mirrors Angular calculateGraduationDate)
        const latestEndDate = termPlans.reduce((latest, tp) => {
            const end = tp.term.endDate ? new Date(tp.term.endDate) : null;
            return end && (!latest || end > latest) ? end : latest;
        }, null);
        const graduationDate = latestEndDate
            ? new Date(latestEndDate.getTime() + 24 * 60 * 60 * 1000)
            : undefined;
        return (0, apiReverseMappers_1.buildSnapPayload)({
            studentIds,
            tenantId,
            organizationId: selectedOrganization.id,
            academicProgramId: selectedProgram.id,
            program: everspringProgram || undefined,
            specialization: everspringSpecialization || undefined,
            track: everspringTrack || undefined,
            graduationDate,
            terms: termPlans.map((tp) => ({
                id: tp.id,
                term: tp.term,
                position: tp.position,
                courses: tp.courses.map((cp) => ({
                    course: cp.course,
                    failed: cp.failed || false,
                })),
            })),
        });
    }, [
        user, selectedOrganization, selectedProgram,
        everspringProgram, everspringSpecialization, everspringTrack, termPlans,
    ]);
    const handleConfirmApply = (0, react_1.useCallback)((ids) => __awaiter(void 0, void 0, void 0, function* () {
        const studentIds = ids !== undefined ? ids : validatedStudentIds;
        setShowConfirmModal(false);
        setShowCohortOnlyModal(false);
        setApplyError(null);
        setIsSubmitting(true);
        try {
            if (!selectedOrganization || !selectedProgram) {
                throw new Error('Organization and program are required');
            }
            const payload = buildCurrentSnapPayload(studentIds);
            yield degreePlanApi_1.degreePlanApi.createSnapPlans(payload);
            const msg = studentIds.length > 0
                ? `Plan created with ${studentIds.length} student(s)!`
                : 'Cohort plan created!';
            success(msg);
            navigate('/degree-planning', {
                state: { orgId: selectedOrganization.id, org: selectedOrganization },
            });
        }
        catch (err) {
            if ((err === null || err === void 0 ? void 0 : err.status) === 400 && (err === null || err === void 0 ? void 0 : err.details)) {
                const normalized = (0, ValidationExceptionsModal_1.normalizeServerErrors)(err.details);
                if ((0, ValidationExceptionsModal_1.hasValidationErrors)(normalized)) {
                    setServerErrors(normalized);
                    setShowErrorModal(true);
                    return;
                }
            }
            setApplyError(err instanceof Error
                ? err.message
                : ((err === null || err === void 0 ? void 0 : err.message) || 'Failed to apply cohort plan. Please try again.'));
        }
        finally {
            setIsSubmitting(false);
        }
    }), [
        validatedStudentIds, selectedOrganization, selectedProgram,
        buildCurrentSnapPayload, success, navigate,
    ]);
    const handleExport = (0, react_1.useCallback)(() => {
        if (!selectedOrganization || !selectedProgram)
            return;
        setIsExporting(true);
        try {
            const tenantId = (user === null || user === void 0 ? void 0 : user.tenantId) || selectedOrganization.tenantId;
            // Match Angular calculateGraduationDate: last term's end date + 1 day.
            const latestEndDate = termPlans.reduce((latest, tp) => {
                const end = tp.term.endDate ? new Date(tp.term.endDate) : null;
                return end && (!latest || end > latest) ? end : latest;
            }, null);
            const graduationDate = latestEndDate
                ? new Date(latestEndDate.getTime() + 24 * 60 * 60 * 1000)
                : undefined;
            // Synthesize a DegreePlan-shaped object purely for the print helper.
            // This is a preview — no studentProgramId, no profile, no audit.
            const previewPlan = {
                id: 'preview',
                type: 'cohort',
                tenantId,
                organization: selectedOrganization,
                academicProgram: selectedProgram,
                cohort: {
                    id: 'preview',
                    name: '',
                    tenantId,
                    organization: selectedOrganization,
                    academicProgram: selectedProgram,
                    program: everspringProgram || undefined,
                    specialization: everspringSpecialization || undefined,
                    track: everspringTrack || undefined,
                },
                terms: [],
                graduationDate,
            };
            // For a preview, the cohort starting-term name is the position-0 term
            // of the current termPlans (since this IS the cohort plan being created).
            const sorted = [...termPlans].sort((a, b) => a.position - b.position);
            const cohortStartTermName = sorted[0]
                ? (sorted[0].term.clientName || sorted[0].term.name)
                : undefined;
            (0, degreePlanPrint_1.printDegreePlan)({
                degreePlan: previewPlan,
                termPlans,
                cohortStartTermName,
            });
        }
        catch (err) {
            showError(err instanceof Error ? err.message : 'Failed to open print dialog');
        }
        finally {
            setIsExporting(false);
        }
    }, [
        selectedOrganization, selectedProgram, user, termPlans,
        everspringProgram, everspringSpecialization, everspringTrack, showError,
    ]);
    const isDataLoading = loadingTerms || loadingCourses;
    return ((0, jsx_runtime_1.jsxs)("div", { className: "snap-page", children: [(0, jsx_runtime_1.jsxs)("div", { className: "snap-page__header", children: [(0, jsx_runtime_1.jsx)("h1", { className: "snap-page__title", children: "Cohort Apply" }), (0, jsx_runtime_1.jsx)("p", { className: "snap-page__description", children: "Create a cohort degree plan and apply it to students" })] }), validationErrors.length > 0 && ((0, jsx_runtime_1.jsx)(ValidationErrors_1.ValidationErrors, { errors: validationErrors, title: "Please fix the following issues:" })), applyError && ((0, jsx_runtime_1.jsxs)("div", { className: "snap-page__error-notice", role: "alert", children: [(0, jsx_runtime_1.jsx)("div", { className: "snap-page__error-notice-icon", "aria-hidden": "true", children: "\u26A0" }), (0, jsx_runtime_1.jsxs)("div", { className: "snap-page__error-notice-body", children: [(0, jsx_runtime_1.jsx)("strong", { className: "snap-page__error-notice-title", children: "Could not apply cohort plan" }), (0, jsx_runtime_1.jsx)("p", { className: "snap-page__error-notice-message", children: applyError })] }), (0, jsx_runtime_1.jsx)("button", { className: "snap-page__error-notice-dismiss", onClick: () => setApplyError(null), "aria-label": "Dismiss error", type: "button", children: "\u00D7" })] })), (0, jsx_runtime_1.jsxs)("div", { className: "snap-page__content", children: [(0, jsx_runtime_1.jsxs)("section", { className: "snap-page__section", "data-number": "1", children: [(0, jsx_runtime_1.jsx)("h2", { className: "snap-page__section-title", children: "1. Cohort Information" }), (0, jsx_runtime_1.jsxs)("div", { style: { display: 'flex', flexDirection: 'column', gap: '1rem' }, children: [(0, jsx_runtime_1.jsx)(OrganizationSelect_1.OrganizationSelect, { value: (selectedOrganization === null || selectedOrganization === void 0 ? void 0 : selectedOrganization.id) || '', onChange: handleOrganizationChange, tenantId: user === null || user === void 0 ? void 0 : user.tenantId, required: true }), selectedOrganization && ((0, jsx_runtime_1.jsx)(ProgramSelect_1.ProgramSelect, { value: (selectedProgram === null || selectedProgram === void 0 ? void 0 : selectedProgram.id) || '', onChange: handleProgramChange, tenantId: selectedOrganization.tenantId, organizationId: selectedOrganization.id, required: true })), selectedProgram && ((0, jsx_runtime_1.jsx)(EverspringMappingSelect_1.EverspringMappingSelect, { academicProgramId: selectedProgram.id, selectedProgram: everspringProgram, selectedSpecialization: everspringSpecialization, selectedTrack: everspringTrack, onProgramChange: setEverspringProgram, onSpecializationChange: setEverspringSpecialization, onTrackChange: setEverspringTrack }))] })] }), selectedProgram && ((0, jsx_runtime_1.jsxs)("section", { className: "snap-page__section", "data-number": "2", children: [(0, jsx_runtime_1.jsx)("h2", { className: "snap-page__section-title", children: "2. Plan" }), isDataLoading ? ((0, jsx_runtime_1.jsx)(LoadingSpinner_1.LoadingSpinner, { label: "Loading terms and courses..." })) : ((0, jsx_runtime_1.jsx)(TermPlanningGrid_1.TermPlanningGrid, { termPlans: termPlans, availableTerms: availableTerms, availableCourses: availableCourses, onChange: setTermPlans }))] })), selectedProgram && ((0, jsx_runtime_1.jsxs)("section", { className: "snap-page__section", "data-number": "3", children: [(0, jsx_runtime_1.jsx)("h2", { className: "snap-page__section-title", children: "3. Students" }), (0, jsx_runtime_1.jsx)(StudentIdTextarea_1.StudentIdTextarea, { value: studentIdsText, onChange: setStudentIdsText })] }))] }), (0, jsx_runtime_1.jsxs)("div", { className: "snap-page__actions", children: [(0, jsx_runtime_1.jsx)(Button_1.Button, { variant: "secondary", onClick: () => navigate('/degree-planning'), children: "Cancel" }), (0, jsx_runtime_1.jsx)(Button_1.Button, { variant: "secondary", onClick: handleExport, loading: isExporting, disabled: isExporting || termPlans.length === 0, children: "Export PDF" }), (0, jsx_runtime_1.jsx)(Button_1.Button, { onClick: handleApply, disabled: isSubmitting ||
                            !selectedOrganization ||
                            !selectedProgram ||
                            termPlans.length === 0, loading: isSubmitting, children: "Apply" })] }), (0, jsx_runtime_1.jsx)(Modal_1.Modal, { isOpen: showConfirmModal, onClose: () => setShowConfirmModal(false), title: "Confirm Apply", footer: (0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(Button_1.Button, { variant: "secondary", onClick: () => setShowConfirmModal(false), children: "Cancel" }), (0, jsx_runtime_1.jsx)(Button_1.Button, { onClick: () => handleConfirmApply(), children: "Apply" })] }), children: (0, jsx_runtime_1.jsxs)("p", { children: ["Are you sure you want to create this cohort plan and apply it to", ' ', validatedStudentIds.length, " student(s)?"] }) }), (0, jsx_runtime_1.jsx)(Modal_1.Modal, { isOpen: showCohortOnlyModal, onClose: () => setShowCohortOnlyModal(false), title: "Create Cohort Without Students?", footer: (0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(Button_1.Button, { variant: "secondary", onClick: () => setShowCohortOnlyModal(false), children: "Cancel" }), (0, jsx_runtime_1.jsx)(Button_1.Button, { onClick: () => handleConfirmApply([]), children: "Create Cohort Only" })] }), children: (0, jsx_runtime_1.jsx)("p", { children: "No student IDs were provided. Would you like to create the cohort plan without assigning any students?" }) }), (0, jsx_runtime_1.jsx)(ValidationExceptionsModal_1.ValidationExceptionsModal, { isOpen: showErrorModal, onClose: () => setShowErrorModal(false), serverErrors: serverErrors })] }));
};
exports.default = SnapPage;
